import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";

import { SettingsProvider } from "@/lib/SettingsContext";
import { useShowEvent } from "@/lib/useShowEvent";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EventBanner from "@/components/EventBanner";
import Home from "@/pages/Home";
import Treatments from "@/pages/Treatments";
import About from "@/pages/About";
import Reviews from "@/pages/Reviews";
import Contact from "@/pages/Contact";
import Gallery from "@/pages/Gallery";
import Review from "@/pages/Review";

// Admin is code-split so public visitors never download the dashboard bundle.
const Admin = lazy(() => import("@/pages/Admin"));

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // If a hash is present (e.g. /#event) let the browser scroll to it
    // instead of forcing top. Small timeout so the target section has
    // mounted by the time we scroll.
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

const Layout = () => {
  const showEvent = useShowEvent();
  const { pathname } = useLocation();
  const bannerVisible = showEvent && !pathname.startsWith("/contact");
  const mainPad = bannerVisible
    ? "pt-40 md:pt-40 lg:pt-44"
    : "pt-28 md:pt-32 lg:pt-36";

  return (
    <>
      <ScrollToTop />
      <EventBanner />
      <Navbar />
      <main className={mainPad}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/treatments" element={<Treatments />} />
          <Route path="/about" element={<About />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/review" element={<Review />} />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<div className="min-h-[50vh]" />}>
                <Admin />
              </Suspense>
            }
          />
        </Routes>
      </main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <SettingsProvider>
        <BrowserRouter>
          <Layout />
          <Toaster
            position="top-center"
            richColors
            toastOptions={{
              style: {
                fontFamily: "Manrope, system-ui, sans-serif",
                borderRadius: "14px",
              },
            }}
          />
        </BrowserRouter>
      </SettingsProvider>
    </div>
  );
}

export default App;
